import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import PlaylistPlayOutlinedIcon from '@mui/icons-material/PlaylistPlayOutlined';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';

interface EvalSidebarProps {
  theme: 'dark' | 'light';
  width: number;
  isCollapsed: boolean;
  isResizing: boolean;
  toggleCollapse: () => void;
  onDragStart: (e: React.MouseEvent) => void;
  toggleTheme: () => void;
  currentPath: string;
}

interface NavSection {
  title: string;
  items: { label: string; icon: React.ReactNode; path: string }[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Runs',
    items: [
      { label: 'All Runs', icon: <PlaylistPlayOutlinedIcon />, path: '/eval/runs' },
      { label: 'Schedule Run', icon: <AddCircleOutlinedIcon />, path: '/eval/runs/new' },
      { label: 'Compare', icon: <CompareArrowsOutlinedIcon />, path: '/eval/runs/compare' },
    ],
  },
  {
    title: 'Data',
    items: [
      { label: 'Datasets', icon: <FolderOutlinedIcon />, path: '/eval/datasets' },
      { label: 'GT Samples', icon: <PhotoLibraryOutlinedIcon />, path: '/eval/gt-samples' },
      { label: 'Import Inference', icon: <DownloadOutlinedIcon />, path: '/eval/pr-inferences' },
    ],
  },
  {
    title: 'Models',
    items: [
      { label: 'OD Models', icon: <AccountTreeOutlinedIcon />, path: '/eval/od-models' },
      { label: 'PR Models', icon: <AccountTreeOutlinedIcon />, path: '/eval/pr-models' },
      { label: 'PC Configs', icon: <TuneOutlinedIcon />, path: '/eval/pc-configs' },
    ],
  },
  {
    title: 'Overview',
    items: [{ label: 'Metrics', icon: <BarChartOutlinedIcon />, path: '/eval/metrics' }],
  },
];

export const EvalSidebar: React.FC<EvalSidebarProps> = ({
  theme,
  width,
  isCollapsed,
  isResizing,
  toggleCollapse,
  onDragStart,
  toggleTheme,
  currentPath,
}) => {
  const { logout } = useAuthContext();
  const navigate = useNavigate();

  const isActive = (path: string) => {
    if (path === '/eval/runs') {
      return currentPath === '/eval/runs' || currentPath === '/eval/runs/';
    }
    return currentPath.startsWith(path);
  };

  const sidebarClasses = [
    'forge-sidebar',
    isCollapsed ? 'is-collapsed' : '',
    isResizing ? 'is-resizing' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside className={sidebarClasses} style={{ width: isCollapsed ? undefined : width }}>
      <nav className="sidebar-nav">
        {NAV_SECTIONS.map((section) => (
          <div key={section.title} style={{ marginBottom: 16 }}>
            <div className="sidebar-section-label">{section.title}</div>
            {section.items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                title={isCollapsed ? item.label : undefined}
              >
                {item.icon}
                <span className="sidebar-item-label">{item.label}</span>
              </Link>
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-footer-utilities">
          <button
            className="sidebar-footer-link"
            onClick={toggleTheme}
            title={
              isCollapsed ? `Switch to ${theme === 'dark' ? 'light' : 'dark'} mode` : undefined
            }
          >
            {theme === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            <span className="sidebar-collapse-label">
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </span>
          </button>

          <button
            className="sidebar-footer-link"
            onClick={() => navigate('/forge/profile')}
            title={isCollapsed ? 'Profile' : undefined}
          >
            <PersonOutlinedIcon />
            <span className="sidebar-collapse-label">Profile</span>
          </button>

          <button
            className="sidebar-footer-link"
            onClick={() => {
              logout();
              navigate('/stores');
            }}
            title={isCollapsed ? 'Sign out' : undefined}
          >
            <LogoutIcon />
            <span className="sidebar-collapse-label">Sign out</span>
          </button>
        </div>

        <div className="sidebar-footer-divider" />

        <button
          className="sidebar-collapse-btn"
          onClick={toggleCollapse}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          <span className="sidebar-collapse-label">Collapse</span>
        </button>
      </div>

      <div className="sidebar-drag-handle" onMouseDown={onDragStart} />
    </aside>
  );
};
